import { useState, useRef, useEffect, useCallback } from "react";
import "../../styles/ImageUpload.css";
import QRCodeModal from "./QRCodeModal";
import { Alert } from "react-bootstrap";
import { useMobileUploadSocket } from "../../hooks/useMobileUploadSocket";

/**
 * ImageUpload Component
 * Provides image file upload functionality with preview and validation
 * Supports desktop upload and mobile QR code scanning for phone uploads
 * @param {function} onImagesChange - Callback with updated images array
 * @param {Array} images - Current images array [{ preview, file, name } or existing { key, url } transformed upstream]
 * @param {number} maxImages - Maximum allowed images
 */
export const ImageUpload = ({
  sampleId,
  onImagesChange,
  images = [],
  maxImages = 3,
  title = "Sample Photos",
  showOptional = false,
  subtitle = "",
}) => {
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [currentPin, setCurrentPin] = useState(null);
  const fileInputRef = useRef(null);
  const previousSampleIdRef = useRef(sampleId);

  useEffect(() => {
    if (previousSampleIdRef.current === sampleId) {
      return;
    }

    previousSampleIdRef.current = sampleId;
    setError("");
    setIsDragging(false);
    setShowQRModal(false);
    setSessionExpired(false);
    setCurrentSessionId(null);
    setCurrentPin(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [sampleId]);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

  // Handle mobile image uploads
  const handleImageReceived = useCallback(
    (newImage) => {
      setError(""); // Clear any previous errors
      onImagesChange([...images, newImage]);
    },
    [images, onImagesChange],
  );

  // Handle session expiration
  const handleSessionExpired = useCallback(() => {
    setSessionExpired(true);
  }, []);

  const handlePinLockout = useCallback(() => {
    setSessionExpired(true);
  }, []);

  // Handle PIN received from server
  const handlePinReceived = useCallback((pin) => {
    console.log("PIN received:", pin);
    setCurrentPin(pin);
  }, []);

  // Use custom hook to manage mobile upload socket
  useMobileUploadSocket({
    sessionId: currentSessionId,
    sampleId,
    onImageReceived: handleImageReceived,
    onSessionExpired: handleSessionExpired,
    onPinLockout: handlePinLockout,
    onPinReceived: handlePinReceived,
    imageCount: images.length,
  });

  const validateFile = (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError("Please upload a valid image (JPEG, PNG, or WebP)");
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      setError("Image size must be less than 5MB");
      return false;
    }
    setError("");
    return true;
  };

  const handleFiles = (fileList) => {
    if (!fileList?.length) return;
    const remaining = maxImages - images.length;
    if (remaining <= 0) {
      setError(`Maximum ${maxImages} images allowed`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);

    // Filter to only valid files first
    const validFiles = files.filter((file) => validateFile(file));

    if (validFiles.length === 0) {
      // All files failed validation, error already set by validateFile
      return;
    }

    // Process all valid files and collect results, then update once
    let loadedCount = 0;
    const newImages = [];
    const failedFiles = [];

    const completeFileProcessing = () => {
      const successfulImages = newImages.filter(Boolean);

      // Set error message if some files failed
      if (failedFiles.length > 0) {
        const failedNames = failedFiles.join(", ");
        setError(
          `Failed to load ${failedFiles.length} file(s): ${failedNames}. ${successfulImages.length} file(s) added successfully.`,
        );
      }

      // Add successful images even if some failed
      if (successfulImages.length > 0) {
        onImagesChange([...images, ...successfulImages]);
      }
    };

    const onProcessingComplete = () => {
      loadedCount++;
      if (loadedCount === validFiles.length) {
        completeFileProcessing();
      }
    };

    validFiles.forEach((file, index) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        newImages[index] = {
          preview: e.target.result,
          file: file,
          name: file.name,
        };
        onProcessingComplete();
      };

      reader.onerror = () => {
        failedFiles.push(file.name);
        onProcessingComplete();
      };

      reader.readAsDataURL(file);
    });
  };

  const handleInputChange = (e) => {
    const fileList = e.target.files;
    handleFiles(fileList);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleRemoveImage = (index) => {
    const next = images.filter((_, i) => i !== index);
    onImagesChange(next);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleQRModalOpen = () => {
    setShowQRModal(true);
    setSessionExpired(false); // Reset expired state when opening modal
  };

  const handleQRSessionCreated = (sessionId) => {
    setCurrentSessionId(sessionId);
    // The useMobileUploadSocket custom hook will automatically register the desktop session
    // when currentSessionId changes in its useEffect
  };

  const slotsRemaining = maxImages - images.length;

  return (
    <>
      <div className='image-upload-container'>
        <h6 className={`${subtitle ? "mb-0" : "mb-3"}`}>
          {title}{" "}
          <small className='fst-italic'>
            ({showOptional ? "optional, " : ""}up to {maxImages})
          </small>
        </h6>
        {subtitle && <p className='fst-italic small mb-2'>{subtitle}</p>}
        {sessionExpired && (
          <Alert variant='warning' className='mb-3'>
            Mobile upload session has expired. Please click 'Upload from Phone'
            to generate a new QR code.
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type='file'
          accept='image/jpeg,image/png,image/webp'
          multiple
          onChange={handleInputChange}
          className='d-none'
        />

        {images.length === 0 ? (
          <div
            className={`upload-area ${isDragging ? "dragging" : ""}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className='upload-content'>
              <i className='bi bi-cloud-arrow-up upload-icon'></i>
              <p className='upload-text'>
                Drag and drop images here or click to browse
              </p>
              <small className='upload-hint'>
                Supported: JPEG, PNG, WebP (Max 5MB) | Max {maxImages} images
              </small>
            </div>
            <div className='upload-buttons-group'>
              <button
                type='button'
                className='btn btn-outline-primary'
                onClick={handleButtonClick}
              >
                <i className='bi bi-folder-open pe-2'></i>
                Choose Images
              </button>
              <button
                type='button'
                className='btn btn-outline-success'
                onClick={handleQRModalOpen}
              >
                <i className='bi bi-qr-code pe-2'></i>
                Upload from Phone
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className='image-grid'>
              {images.map((img, idx) => (
                <div key={idx} className='image-preview-container'>
                  <div className='image-preview-wrapper'>
                    <img
                      src={img.preview || img.url}
                      alt={`Sample preview ${idx + 1}`}
                      className='image-preview'
                    />
                  </div>
                  <div className='image-info'>
                    <p className='mb-1'>
                      <strong>Image {idx + 1}</strong>
                    </p>
                    {img.file && (
                      <small className='text-muted'>
                        {(img.file.size / 1024).toFixed(2)} KB
                      </small>
                    )}
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-outline-danger'
                    onClick={() => handleRemoveImage(idx)}
                  >
                    <i className='bi bi-trash pe-1'></i>
                    Remove
                  </button>
                </div>
              ))}
            </div>
            {slotsRemaining > 0 && (
              <div className='upload-buttons-group'>
                <button
                  type='button'
                  className='btn btn-outline-primary'
                  onClick={handleButtonClick}
                >
                  <i className='bi bi-plus-circle pe-2'></i>
                  Add another (up to {slotsRemaining} more)
                </button>
                <button
                  type='button'
                  className='btn btn-outline-success'
                  onClick={handleQRModalOpen}
                >
                  <i className='bi bi-qr-code pe-2'></i>
                  Upload from Phone
                </button>
              </div>
            )}
          </>
        )}

        {error && (
          <div className='alert alert-danger alert-sm mt-2'>{error}</div>
        )}
      </div>

      <QRCodeModal
        show={showQRModal}
        onHide={() => setShowQRModal(false)}
        onSessionCreated={handleQRSessionCreated}
        sessionExpired={sessionExpired}
        onRegenerateSession={() => setSessionExpired(false)}
        pin={currentPin}
      />
    </>
  );
};
